/**
 * Per-section screener results (first check-in). Shows each answered section's
 * band — never a single combined score (D5/PRD §8.34) — and a truthful closing
 * message that the care team *will review* the responses (not that they already
 * have). Follow-up progression (Part B) is out of scope for the MVP (PRD §16.2).
 * @agentic members
 */

import {
  Paper,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Divider,
} from "@mantine/core";

/**
 * One section's readback for the results summary. A band (`severityDisplay`)
 * is present only when the section was fully completed — a partially answered
 * section is shown as a readback with no score and no band (PRD D3 / §8.9).
 */
export type ScreenerResultCard = {
  sectionKey: string;
  label: string;
  /** True only when every item in the instrument was answered. */
  complete: boolean;
  /** The resolved five-state outcome (§8.8); drives the non-scored read-back. */
  outcome?: string;
  /** Patient-facing read-back for a non-completed section (§8.9). */
  stateLabel?: string;
  total?: number;
  maxScore?: number;
  severityDisplay?: string;
  interpretation?: string;
};

type ScreenerResultsProps = {
  cards: ScreenerResultCard[];
  /**
   * When the administration included a positive self-harm answer, crisis
   * resources repeat beneath the results (PRD §8.21).
   */
  crisisTriggered?: boolean;
};

/**
 * Results summary shown after the screener is completed.
 * @agentic members
 */
export const ScreenerResults = ({
  cards,
  crisisTriggered = false,
}: ScreenerResultsProps) => (
  <Paper radius="lg" p="xl" shadow="sm">
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={2} c="#391640">
          Thank you for completing your screener
        </Title>
        <Text style={{ opacity: 0.6, color: "#2a1a2e" }}>
          Here is a summary of your responses. Your care team will review them
          and follow up with you.
        </Text>
      </Stack>

      {cards.length > 0 && <Divider />}

      <Stack gap="md">
        {cards.map((card) => (
          <Group
            key={`${card.sectionKey}-${card.label}`}
            data-testid={`screener-result-${card.sectionKey}`}
            justify="space-between"
            align="flex-start"
            gap="xs"
            wrap="wrap"
          >
            <Stack gap={2} style={{ flex: "1 1 12rem", minWidth: 0 }}>
              <Text fw={600} c="#391640" style={{ overflowWrap: "anywhere" }}>
                {card.label}
              </Text>
              {card.complete && card.interpretation && (
                <Text size="sm" style={{ opacity: 0.55, color: "#2a1a2e" }}>
                  {card.interpretation}
                </Text>
              )}
              {!card.complete && (
                <Text size="sm" style={{ opacity: 0.55, color: "#2a1a2e" }}>
                  {card.stateLabel ?? "Not completed — no result shown"}
                </Text>
              )}
            </Stack>
            {card.complete && card.severityDisplay && (
              <Badge size="lg" variant="light" color="plum">
                {card.severityDisplay}
              </Badge>
            )}
          </Group>
        ))}
      </Stack>

      {crisisTriggered && (
        <>
          <Divider />
          <Paper radius="md" p="md" bg="#fdecea" withBorder>
            <Stack gap="xs">
              <Text fw={700} c="#7a1f13">
                If you are thinking about harming yourself, help is available
                now
              </Text>
              <Text size="sm" style={{ color: "#7a1f13" }}>
                Call or text the 988 Suicide &amp; Crisis Lifeline (call or text
                988) any time, day or night. If you are in immediate danger,
                call 911 or go to the nearest emergency room.
              </Text>
            </Stack>
          </Paper>
        </>
      )}

      <Divider />
      <Text size="sm" ta="center" style={{ opacity: 0.45, color: "#2a1a2e" }}>
        If this is an emergency, please call 911 or visit the nearest emergency
        room. You can also contact the 988 Suicide &amp; Crisis Lifeline by
        calling or texting 988.
      </Text>
    </Stack>
  </Paper>
);
